import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FormSection } from '@/components';
import { ADMIN_COLORS } from '@/constants/admin';
import { roleNamesAndDescriptions } from '@/types/admin/userManagement';

interface UserPreviewSectionProps {
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    roleName: string;
    organizations?: string[];
    properties?: string[];
  };
}

export const UserPreviewSection: React.FC<UserPreviewSectionProps> = ({
  formData,
}) => {
  // Find the role details
  const roleDetails = Object.values(roleNamesAndDescriptions).find(
    role => role.value === formData.roleName
  );

  return (
    <>
      {/* Step Indicator */}
      <View style={styles.stepIndicator}>
        <Text style={styles.stepText}>
          Step 3 of 3: Preview
        </Text>
      </View>

      <FormSection 
        title="Review User Details"
        subtitle="Please review the information before creating the user"
      >
      <View style={styles.container}>
        {/* Role Information */}
        <View style={styles.roleCard}>
          <Text style={styles.roleTitle}>{roleDetails?.name || formData.roleName}</Text>
          <Text style={styles.roleDescription}>
            {roleDetails?.description || 'Custom role permissions'}
          </Text>
        </View>

        {/* User Information */}
        <View style={styles.infoSection}>
          <InfoField label="First Name" value={formData.firstName} />
          <InfoField label="Last Name" value={formData.lastName} />
          <InfoField label="Email Address" value={formData.email} />
        </View>

        {/* Organizations (if applicable) */}
        {formData.organizations && formData.organizations.length > 0 && (
          <View style={styles.infoSection}>
            <Text style={styles.sectionTitle}>Related Organizations</Text>
            {formData.organizations.map((org, index) => (
              <Text key={index} style={styles.listItem}>• {org}</Text>
            ))}
          </View>
        )}

        {/* Properties (if applicable) */}
        {formData.properties && formData.properties.length > 0 && (
          <View style={styles.infoSection}>
            <Text style={styles.sectionTitle}>Related Properties</Text>
            {formData.properties.map((prop, index) => (
              <Text key={index} style={styles.listItem}>• {prop}</Text>
            ))}
          </View>
        )}
      </View>
      </FormSection>
    </>
  );
};

interface InfoFieldProps {
  label: string;
  value: string;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => {
  return (
    <View style={styles.infoField}>
      <Text style={styles.infoLabel}>{label.toUpperCase()}</Text>
      <Text style={styles.infoValue}>{value}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  stepIndicator: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    marginBottom: 16,
  },
  stepText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
  container: {
    gap: 24,
  },
  roleCard: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  roleTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  roleDescription: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 20,
  },
  infoSection: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  infoField: {
    gap: 4,
  },
  infoLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_TERTIARY,
    letterSpacing: 0.5,
  },
  infoValue: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  listItem: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginLeft: 8,
  },
});
